import detailMessage from './detailMessage';
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import { fetchCancelPreview, cancelAppointmentCustomer } from "./api";
import ConfirmShell from "./components/ConfirmShell";
import { useLang } from "./LangContext";

interface CancelPreviewData {
  status: string;
  artist_name: string;
  start_time: string;
  duration: number;
  worker_slug?: string;
}

type PageStatus = "loading" | "preview" | "done" | "already-cancelled" | "not-cancellable" | "error";

interface PageState {
  status: PageStatus;
  data?: CancelPreviewData;
  message?: string;
}

function formatAppointment(startIso: string, duration: number): string {
  if (!startIso) return "";
  return `${format(new Date(startIso), 'dd.MM.yy HH:mm')} (${duration}h)`;
}

export default function CancelPage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useLang();
  const [state, setState] = useState<PageState>({ status: "loading" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchCancelPreview(token!)
      .then((data: CancelPreviewData) => {
        if (cancelled) return;
        if (data.status === "CANCELLED") {
          setState({ status: "already-cancelled", data });
        } else if (data.status === "DECLINED") {
          setState({ status: "not-cancellable", data });
        } else {
          setState({ status: "preview", data });
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const detail = detailMessage(err, "Could not load cancellation");
        setState({ status: "error", message: detail });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const doCancel = async () => {
    setSubmitting(true);
    try {
      await cancelAppointmentCustomer(token!);
      setState((prev) => ({ status: "done", data: prev.data }));
    } catch (err: unknown) {
      const detail = detailMessage(err, "Cancellation failed");
      setState({ status: "error", message: detail });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfirmShell>
      {state.status === "loading" && (
        <>
          <div className="confirm-spinner" aria-hidden="true" />
          <h2>{t.loadingAppointment}</h2>
        </>
      )}

      {state.status === "preview" && (
        <>
          <h2>{t.cancelQuestion}</h2>
          <p>
            <strong>{state.data!.artist_name}</strong>
            <br />
            {formatAppointment(state.data!.start_time, state.data!.duration)}
          </p>
          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", marginTop: "1.5rem", flexWrap: "wrap" }}>
            <button onClick={doCancel} disabled={submitting} className="button logout">
              {submitting ? t.cancelling : t.yesCancel}
            </button>
            {state.data!.worker_slug && (
              <a href={`/book/${state.data!.worker_slug}?reschedule=${token}`} className="button button-secondary">
                {t.rescheduleInstead}
              </a>
            )}
          </div>
          <p className="close-tab-hint">{t.noCancel}</p>
        </>
      )}

      {state.status === "done" && (
        <>
          <div className="confirm-status-icon ok" aria-hidden="true">✓</div>
          <h2>{t.appointmentCancelled}</h2>
          <p>{t.appointmentCancelledMessage}</p>
          {state.data?.worker_slug && (
            <p>
              <a href={`/book/${state.data.worker_slug}`} className="button button-secondary">
                {t.bookNewAppointment}
              </a>
            </p>
          )}
          <p className="close-tab-hint">{t.closeTab}</p>
        </>
      )}

      {state.status === "already-cancelled" && (
        <>
          <div className="confirm-status-icon ok" aria-hidden="true">✓</div>
          <h2>{t.alreadyCancelled}</h2>
          <p>{t.alreadyCancelledMessage}</p>
          <p className="close-tab-hint">{t.closeTab}</p>
        </>
      )}

      {state.status === "not-cancellable" && (
        <>
          <div className="confirm-status-icon error" aria-hidden="true">!</div>
          <h2>{t.nothingToCancel}</h2>
          <p>{t.notCancellableMessage}</p>
          <p className="close-tab-hint">{t.closeTab}</p>
        </>
      )}

      {state.status === "error" && (
        <>
          <div className="confirm-status-icon error" aria-hidden="true">!</div>
          <h2>{t.somethingWentWrong}</h2>
          <p>{state.message}</p>
          <p className="close-tab-hint">{t.closeTab}</p>
        </>
      )}
    </ConfirmShell>
  );
}
